import { usePage } from "@inertiajs/react";
import { debounce } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import * as React from "react";
import { useLayoutEffect } from "react";

import { RendererProps } from "../Input";

const DEBOUNCE = 400;

interface Option {
  id: string;
  label: string;
}

const fetchStations = debounce(
  async (
    baseUrl: string,
    query: string,
    callback: (results?: readonly Option[]) => void,
  ) => {
    const suggestions: Record<string, Record<string, string>>[] = await fetch(
      `${baseUrl}/trains/stations?query=${query}`,
      {
        method: "GET",
      },
    )
      .then((response) => response.json())
      .catch(console.error);

    callback(
      suggestions.map(({ id: { code }, names: { long } }) => ({
        label: long,
        id: code,
      })),
    );
  },
  DEBOUNCE,
);

const StationInput = ({ name, onChange, placeholder }: RendererProps) => {
  const { baseUrl } = usePage<{ baseUrl: string }>().props;
  const [value, setValue] = React.useState<Option | null>(null);
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState<readonly Option[]>([]);

  useLayoutEffect(() => {
    if (inputValue === "") {
      setOptions(value ? [value] : []);

      return undefined;
    }

    // Allow to resolve the out of order request resolution.
    let active = true;

    fetchStations(baseUrl, inputValue, (results?: readonly Option[]) => {
      if (!active) {
        return;
      }

      let newOptions: readonly Option[] = [];

      if (results) {
        newOptions = results;

        if (value) {
          newOptions = [
            value,
            ...results.filter((result) => result.label !== value.label),
          ];
        }
      } else if (value) {
        newOptions = [value];
      }
      setOptions(newOptions);
    });

    return () => {
      active = false;
    };
  }, [value, inputValue]);

  return (
    <Autocomplete
      autoComplete
      filterSelectedOptions
      includeInputInList
      filterOptions={(x) => x}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.label
      }
      noOptionsText="Geen resultaten"
      options={options}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          name={name}
          placeholder={placeholder}
        />
      )}
      value={value}
      onChange={(_event, newValue: Option | null) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        onChange?.({
          target: {
            name,
            value: newValue?.label ?? "",
            stationId: newValue?.id,
          },
        });
      }}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
    />
  );
};

export default StationInput;
